import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CreateAiPill from './CreateAiPill';
import CreateCloseButton from './CreateCloseButton';

/**
 * CreatePopup — modal-overlay, показывается над Home при tap «Создать» в BottomNav.
 *
 * Figma source: `318:3434` (`modal-overlay`), 402×809 absolute над Home 318:3274.
 *
 * Структура (figma-spec §2 + modal-overlay.json):
 *   - Backdrop: full-screen black α=0.64 (token `create-backdrop`).
 *   - Row 328×88 (itemSpacing 8, центр по X) — 2 × `<CreateAiPill>`.
 *     Figma y=633 в 809-высокого frame → bottom=88px от низа.
 *   - Close 115×48 внизу (Figma y=741) → bottom=20px от низа.
 *
 * КРИТИЧНО (user feedback 2026-04-19): close pill должен **пixel-точно
 * перекрывать** «Создать» UIButton из BottomNav. Это требует:
 *   (a) close и "Создать" одной ширины 115×48 (Navbar spec),
 *   (b) обе кнопки center-horizontal с одинаковым bottom-offset,
 *   (c) z-index close выше чем у BottomNav (z-40) → используем z-60.
 *
 * BottomNav layout (закреплён):
 *   fixed bottom-0 pt-4 pb=calc(safe+12px); inside Navbar p-1 h-12 (inner 48px).
 *   "Создать" UIButton vertical center = safe + 12 + 4 + 24 = safe + 40px.
 *   Значит "Создать" bottom = safe + 16px (40 - 24).
 *
 * Close позиционирование — совпадает с "Создать":
 *   fixed bottom=calc(safe+16px) left-1/2 -translate-x-1/2 z-60.
 *
 * Pills row — над close с gap 20px (Figma):
 *   fixed bottom=calc(safe+16+48+20px)=calc(safe+84px) z-55.
 *
 * URL-managed open/close живёт в AppLayout (`?create=1`); мы только
 * рендерим/скрываем по `open` prop. Escape/backdrop/close → onClose.
 */
export default function CreatePopup({ open, onClose, onPickPhoto, onPickVideo }) {
  const { t } = useTranslation();
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={t('create.chooseType', 'Выберите тип генерации')}
    >
      <button
        type="button"
        data-testid="create-popup-backdrop"
        onClick={onClose}
        aria-label={t('common.close', 'Закрыть')}
        className="absolute inset-0 bg-create-backdrop"
      />
      {/* Row of 2 AI pills — bottom = safe + 84px (above close) */}
      <div
        data-testid="create-popup-content"
        onClick={(e) => e.stopPropagation()}
        className="fixed left-0 right-0 z-[55] flex justify-center pointer-events-none"
        style={{ bottom: 'calc(var(--safe-bottom, 0px) + 84px)' }}
      >
        <div className="flex gap-2 pointer-events-auto">
          <CreateAiPill variant="photo" onClick={onPickPhoto} />
          <CreateAiPill variant="video" onClick={onPickVideo} />
        </div>
      </div>
      {/* Close pill — overlays BottomNav "Создать" pixel-perfect (115×48 same pos) */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="fixed left-0 right-0 z-[60] flex justify-center pointer-events-none"
        style={{ bottom: 'calc(var(--safe-bottom, 0px) + 16px)' }}
      >
        <div className="pointer-events-auto">
          <CreateCloseButton onClick={onClose} />
        </div>
      </div>
    </div>
  );
}
